// @ts-ignore:next-line need type definition file for vp/auth
import VistaprintAuth from '@vp/auth';
import { observableFetchClient } from '@vp/common-api-utils';

const REQUEST_METHODS = Object.freeze({
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  DELETE: 'delete',
});

/**
 * Helper function for building out header object
 * @returns { Headers }
 */
const buildStandardHeaders = (): Headers => {
  const auth = new VistaprintAuth.WebAuth();
  const headerOptions: HeadersInit = {
    'Access-Control-Allow-Origin': '*',
    Authorization: auth.getAuthorizationHeader() || '',
  };
  const headers = new Headers(headerOptions);
  return headers;
};

/**
 * Helper function for building out header object that requires a application/json content-type
 */
const buildStandardJsonHeaders = (): Headers => {
  const headers = buildStandardHeaders();
  headers.append('Content-Type', 'application/json');
  return headers;
};

export type GetParams = Record<string, string | boolean | number>;

export type GetRequest = {
  url: string;
  params?: GetParams;
};

/**
 * Dispatches a GET REST request.
 * Usage Note - if your params object contains an array, it is recommended to use the 'qs'
 * library until the 'observableFetchClient' handles array params.
 * @example
  const searchParams = qs.stringify(
    { arrParam: ['best', 'params', 'ever'] },
    { arrayFormat: 'repeat' }
  );
  const url = `${baseUrl}?${searchParams}`;
  dispatchGetRequest({ url });
 */
export const dispatchGetRequest = async ({
  url,
  params = {},
}: GetRequest): Promise<Response> =>
  observableFetchClient({
    url,
    params,
    method: REQUEST_METHODS.GET,
    headers: buildStandardHeaders(),
  });

export type PostRequest = {
  url: string;
  body?: Array<string> | Record<string, any>;
  params?: Record<string, any>;
};

/**
 * Dispatches a POST REST request
 */
export const dispatchPostRequest = async ({
  url,
  body = {},
  params = {},
}: PostRequest): Promise<Response> =>
  observableFetchClient({
    url,
    params,
    body: JSON.stringify(body),
    method: REQUEST_METHODS.POST,
    headers: buildStandardJsonHeaders(),
  });

export type PutRequest = {
  url: string;
  timeout: number;
};

/**
 * Dispatches a PUT REST request with no body
 */
export const dispatchPutRequest = async ({
  url,
  timeout,
}: PutRequest): Promise<Response> =>
  observableFetchClient({
    url,
    timeout,
    method: REQUEST_METHODS.PUT,
    headers: buildStandardHeaders(),
  });

/**
 * Dispatches a PUT REST request that includes a body
 *
 * added this because the dispatchPutRequest takes URL params and
 * I didn't want to break the VIG handling by mistake
 *
 */
export const dispatchPutWithBodyRequest = async ({
  url,
  body = {},
  params = {},
}: PutWithBodyRequest): Promise<Response> =>
  observableFetchClient({
    url,
    params,
    body: JSON.stringify(body),
    method: REQUEST_METHODS.PUT,
    headers: buildStandardJsonHeaders(),
  });

export type PutWithBodyRequest = {
  url: string;
  body?: Array<string> | Record<string, any>;
  params?: Record<string, any>;
};

export type DeleteRequest = {
  url: string;
  body?: Array<string> | Record<string, any>;
  params?: Record<string, any>;
};

/**
 * Dispatches a DELETE REST request
 */
export const dispatchDeleteRequest = async ({
  url,
  body = {},
  params = {},
}: DeleteRequest): Promise<Response> =>
  observableFetchClient({
    url,
    params,
    body: JSON.stringify(body),
    method: REQUEST_METHODS.DELETE,
    headers: buildStandardJsonHeaders(),
  });
